import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Trash2, Users, Linkedin, Mail, MessageCircle, Calendar, AlertTriangle } from "lucide-react";

const TYPES = [
  { key: "recruiter", label: "Recruiter", color: "bg-purple-500" },
  { key: "hiring_manager", label: "Hiring Manager", color: "bg-primary" },
  { key: "mentor", label: "Mentor", color: "bg-emerald-500" },
  { key: "peer", label: "Peer", color: "bg-zinc-500" },
];

const typeMeta = (t) => TYPES.find(x => x.key === t) || TYPES[3];
const daysUntil = (dateStr) => { if (!dateStr) return null; const d = new Date(dateStr); if (isNaN(d)) return null; return Math.round((d.getTime() - Date.now()) / 86400000); };
const emptyForm = { name: "", role: "", company: "", contact_type: "peer", email: "", linkedin_url: "", notes: "", next_touch_date: "" };

export default function Network() {
  const [contacts, setContacts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);
  const load = () => api.get("/contacts").then(r => setContacts(r.data));

  const add = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    setSaving(true);
    try {
      await api.post("/contacts", form);
      setForm(emptyForm); setShowAdd(false); load(); toast.success("Contact added");
    } catch { toast.error("Add failed"); } finally { setSaving(false); }
  };

  const del = async (id) => { if (!window.confirm("Delete contact?")) return; try { await api.delete(`/contacts/${id}`); load(); } catch { toast.error("Delete failed"); } };
  const logTouch = async (id) => { try { await api.post(`/contacts/${id}/logged`); load(); toast.success("Marked as contacted today"); } catch { toast.error("Failed"); } };

  const filtered = filter === "all" ? contacts : contacts.filter(c => c.contact_type === filter);
  const dueSoon = contacts.filter(c => {
    const d = daysUntil(c.next_touch_date);
    return d !== null && d <= 3;
  });

  return (
    <div className="p-10 max-w-6xl" data-testid="network-page">
      <div className="flex justify-between items-start mb-8">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">Module 09</div>
          <h1 className="font-display font-bold text-5xl tracking-tight">Network</h1>
          <p className="text-muted-foreground mt-2 text-sm">Your career is 70% who you know. Keep the graph warm.</p>
        </div>
        <button data-testid="add-contact-btn" onClick={()=>setShowAdd(true)} className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-semibold transition-colors px-4 py-2.5 text-sm flex items-center gap-2"><Plus size={14}/> Add contact</button>
      </div>

      {dueSoon.length > 0 ? (
        <div className="card-flat p-5 mb-6 border-l-2 border-l-amber-500" data-testid="follow-up-panel">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={16} className="text-amber-400"/>
            <div className="font-display font-bold text-lg">Follow-ups due ({dueSoon.length})</div>
          </div>
          <div className="grid md:grid-cols-2 gap-2">
            {dueSoon.map(c => {
              const d = daysUntil(c.next_touch_date);
              return (
                <div key={c.id} className="flex items-center justify-between border border-border p-3" data-testid={`reminder-${c.id}`}>
                  <div className="min-w-0">
                    <div className="font-display font-bold text-sm truncate">{c.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{c.role} @ {c.company}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className={`text-xs font-mono ${d < 0 ? "text-red-400" : "text-amber-400"}`}>{d < 0 ? `${Math.abs(d)}d overdue` : d === 0 ? "today" : `${d}d`}</div>
                    <button data-testid={`reminder-log-${c.id}`} onClick={()=>logTouch(c.id)} className="text-xs px-2 py-1 bg-emerald-500/20 border border-emerald-500/40 text-emerald-300">Logged</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div data-testid="follow-up-panel" className="card-flat p-4 mb-6 text-xs font-mono uppercase tracking-widest text-muted-foreground">No follow-ups due · Add a next-touch-date to trigger reminders</div>
      )}

      <div className="flex flex-wrap gap-2 mb-6">
        <FilterBtn active={filter==="all"} onClick={()=>setFilter("all")} label={`All (${contacts.length})`} testId="filter-all"/>
        {TYPES.map(t => (
          <FilterBtn key={t.key} active={filter===t.key} onClick={()=>setFilter(t.key)} label={`${t.label} (${contacts.filter(c=>c.contact_type===t.key).length})`} testId={`filter-${t.key}`}/>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="card-flat p-10 text-center text-muted-foreground">No contacts yet. Add your first — recruiters, hiring managers, mentors.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3" data-testid="contacts-grid">
          {filtered.map(c => {
            const meta = typeMeta(c.contact_type);
            const lastDays = c.last_contacted ? Math.round((Date.now() - new Date(c.last_contacted).getTime())/86400000) : null;
            const nextDays = daysUntil(c.next_touch_date);
            return (
              <div key={c.id} className="card-flat p-4 group" data-testid={`contact-${c.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1"><div className={`w-1.5 h-1.5 ${meta.color}`}/> <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{meta.label}</span></div>
                    <div className="font-display font-bold text-base truncate">{c.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{c.role}{c.role && c.company ? " · " : ""}{c.company}</div>
                  </div>
                  <button onClick={()=>del(c.id)} className="text-muted-foreground/60 hover:text-red-400 opacity-0 group-hover:opacity-100"><Trash2 size={14}/></button>
                </div>
                {c.notes && <div className="text-xs text-muted-foreground mb-2 line-clamp-2">{c.notes}</div>}
                <div className="flex items-center gap-3 text-[10px] font-mono uppercase text-muted-foreground">
                  {c.email && <a href={`mailto:${c.email}`} className="hover:text-foreground flex items-center gap-1"><Mail size={11}/> Email</a>}
                  {c.linkedin_url && <a href={c.linkedin_url} target="_blank" rel="noreferrer" className="hover:text-foreground flex items-center gap-1"><Linkedin size={11}/> LinkedIn</a>}
                  {lastDays !== null && <span>Contacted {lastDays}d ago</span>}
                  {nextDays !== null && <span className={nextDays < 0 ? "text-red-400" : nextDays <= 7 ? "text-amber-400" : ""}>Next in {nextDays}d</span>}
                </div>
                <button data-testid={`contact-log-${c.id}`} onClick={()=>logTouch(c.id)} className="mt-3 text-xs btn-ghost w-full py-1.5">Log a chat today</button>
              </div>
            );
          })}
        </div>
      )}

      {showAdd && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="add-contact-modal">
          <div className="card-flat p-6 max-w-lg w-full space-y-3">
            <div className="flex items-center justify-between mb-2">
              <div className="font-display font-bold text-2xl tracking-tight">Add contact</div>
              <button onClick={()=>{setShowAdd(false); setForm(emptyForm);}}><X size={18}/></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <F label="Name" v={form.name} on={v=>setForm({...form, name:v})} tid="cf-name"/>
              <F label="Role" v={form.role} on={v=>setForm({...form, role:v})} tid="cf-role"/>
              <F label="Company" v={form.company} on={v=>setForm({...form, company:v})} tid="cf-company"/>
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Type</label>
                <select data-testid="cf-type" value={form.contact_type} onChange={e=>setForm({...form, contact_type:e.target.value})} className="w-full mt-2 bg-card border border-border px-3 py-2.5">
                  {TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </div>
              <F label="Email" v={form.email} on={v=>setForm({...form, email:v})} tid="cf-email"/>
              <F label="LinkedIn URL" v={form.linkedin_url} on={v=>setForm({...form, linkedin_url:v})} tid="cf-linkedin"/>
              <div className="col-span-2">
                <F label="Next follow-up date" type="date" v={form.next_touch_date} on={v=>setForm({...form, next_touch_date:v})} tid="cf-next"/>
              </div>
              <div className="col-span-2">
                <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Notes</label>
                <textarea data-testid="cf-notes" value={form.notes} onChange={e=>setForm({...form, notes:e.target.value})} rows={3} className="w-full mt-2 bg-card border border-border px-3 py-2"/>
              </div>
            </div>
            <button data-testid="cf-submit" onClick={add} disabled={saving} className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-semibold transition-colors w-full py-2.5">{saving? "Adding...":"Add contact"}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function F({ label, v, on, type="text", tid }) {
  return (
    <div>
      <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{label}</label>
      <input data-testid={tid} type={type} value={v} onChange={e=>on(e.target.value)} className="w-full mt-2 bg-card border border-border px-3 py-2.5"/>
    </div>
  );
}
function FilterBtn({ active, onClick, label, testId }) {
  return <button data-testid={testId} onClick={onClick} className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest border ${active?"bg-primary border-primary text-foreground":"bg-transparent border-border text-muted-foreground hover:border-zinc-500"}`}>{label}</button>;
}
